import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface CrmReminder { id: string; target_id: string; body: string; remind_at: string; type: string }

/** Open reminders for the workspace (due + upcoming). Polls every minute; toasts newly-due ones. */
export const useCrmReminders = (wsId?: string, opts: { toast?: boolean } = {}) => {
  const q = useQuery({
    queryKey: ["crm-reminders", wsId], enabled: !!wsId, refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("lead_activities")
        .select("id, target_id, body, remind_at, type").eq("workspace_id", wsId)
        .not("remind_at", "is", null).is("done_at", null).order("remind_at").limit(500);
      if (error) throw error;
      return (data ?? []) as CrmReminder[];
    },
  });
  const shown = useRef(new Set<string>());
  const list = q.data ?? [];
  const now = Date.now();
  const due = list.filter((r) => new Date(r.remind_at).getTime() <= now);

  useEffect(() => {
    if (!opts.toast) return;
    due.forEach((r) => {
      if (shown.current.has(r.id)) return;
      shown.current.add(r.id);
      toast(`Reminder: ${r.body.slice(0, 80)}`, { duration: 8000 });
    });
  }, [due, opts.toast]);

  const byTarget = new Map<string, CrmReminder>();
  list.forEach((r) => { if (!byTarget.has(r.target_id)) byTarget.set(r.target_id, r); });
  return { reminders: list, due, byTarget };
};
